import pusherClient from "@/lib/pusher-client"

import { v4 as uuid } from "uuid"
import { isEmpty } from "@/lib/utils"

import { MESSAGE_TYPES, MESSAGE_REACTION_TYPES } from "@/lib/constants"

import pusherEvents from "@shared/pusher-events.json"

export const CHANNEL_EVENTS = pusherEvents

export { MESSAGE_TYPES, MESSAGE_REACTION_TYPES }

const wrapCallback = callback => val => (callback ? callback(val) : null)

const bindEvent = (channel, instance, event, callback) => {
  channel.bind(event, callback)
  channel._instanceCallbacks = channel._instanceCallbacks || {}
  channel._instanceCallbacks[instance] =
    channel._instanceCallbacks[instance] || {}
  channel._instanceCallbacks[instance][event] = callback
}

const teardownChannelInstance = (channel, instance) => {
  const callbacks = channel._instanceCallbacks[instance]

  // Remove all callbacks defined on this instance
  Object.keys(callbacks).forEach(eventName => {
    channel.unbind(eventName, callbacks[eventName])
    delete channel._instanceCallbacks[eventName]
  })

  // If there are no other listeners on the channel, we can unsubscribe completely
  if (isEmpty(channel.callbacks._callbacks)) {
    pusherClient.unsubscribe(channel.name)
  }
}

export const subscribeToThread = async (
  channelId,
  callbacks = {
    onMessage: () => {},
    onMessageDeleted: () => {},
    onMessageUpdated: () => {},
    onMessageReaction: () => {},
    onThreadUpdated: () => {},
    onSubscribe: () => {},
    onMemberAdded: () => {}, // Member actively viewing
    onMemberRemoved: () => {}, // Member stopped viewing
    onMemberBlocked: () => {}, // Member blocked by another one
    onFormSubmitted: () => {},
    onParticipantAdded: () => {}, // Member joined thread
    onParticipantRemoved: () => {}, // Member left thread
    onTyping: () => {}
  },
  presence = true
) => {
  const instance = uuid()
  let [channel, presenceChannel] = pusherClient.subscribe(
    { channelId },
    presence
  )

  await new Promise((resolve, reject) => {
    channel.bind("pusher:subscription_succeeded", () => {
      resolve()
    })

    channel.bind("pusher:subscription_error", () => {
      setTimeout(() => {
        if (channel.subscribed) {
          return
        }

        pusherClient.unsubscribe(channel.name)
        if (presenceChannel) {
          pusherClient.unsubscribe(presenceChannel.name)
        }

        ;[channel, presenceChannel] = pusherClient.subscribe(
          { channelId },
          presence
        )

        channel.bind("pusher:subscription_succeeded", () => {
          resolve()
        })

        channel.bind("pusher:subscription_error", () => {
          reject()
        })
      }, 1500)
    })
  })

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.MESSAGE,
    wrapCallback(callbacks.onMessage)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.MESSAGE_REACTION,
    wrapCallback(callbacks.onMessageReaction)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.MESSAGE_UPDATED,
    wrapCallback(callbacks.onMessageUpdated)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.MESSAGE_DELETED,
    wrapCallback(callbacks.onMessageDeleted)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.PARTICIPANT_REMOVED,
    wrapCallback(callbacks.onParticipantRemoved)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.THREAD_UPDATED,
    wrapCallback(callbacks.onThreadUpdated)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.FORM_SUBMITTED,
    wrapCallback(callbacks.onFormSubmitted)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.PARTICIPANT_ADDED,
    wrapCallback(callbacks.onParticipantAdded)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.MEMBER_BLOCKED,
    wrapCallback(callbacks.onMemberBlocked)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.TYPING,
    wrapCallback(callbacks.onTyping)
  )

  if (presenceChannel) {
    bindEvent(
      presenceChannel,
      instance,
      CHANNEL_EVENTS.SUBSCRIPTION_SUCCEEDED,
      wrapCallback(callbacks.onSubscribe)
    )

    bindEvent(
      presenceChannel,
      instance,
      CHANNEL_EVENTS.MEMBER_ADDED,
      wrapCallback(callbacks.onMemberAdded)
    )

    bindEvent(
      presenceChannel,
      instance,
      CHANNEL_EVENTS.MEMBER_REMOVED,
      wrapCallback(callbacks.onMemberRemoved)
    )
  }

  return { instance, channel, presenceChannel }
}

export const subscribeToNotifications = async (
  channelId,
  callbacks = {
    onNotificationRead: () => {},
    onNotificationArchived: () => {},
    onNotificationCreated: () => {}
  }
) => {
  const instance = uuid()
  const [channel, presenceChannel] = pusherClient.subscribe(
    { channelId },
    false
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.NOTIFICATION_MARKED_READ,
    wrapCallback(callbacks.onNotificationRead)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.NOTIFICATION_ARCHIVED,
    wrapCallback(callbacks.onNotificationArchived)
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.NOTIFICATION_CREATED,
    wrapCallback(callbacks.onNotificationCreated)
  )

  return { instance, channel, presenceChannel }
}

export const subscribeToSegment = async (
  channelId,
  callbacks = {
    onSegmentUpdated: () => {}
  }
) => {
  const instance = uuid()
  const [channel, presenceChannel] = pusherClient.subscribe(
    { channelId },
    false
  )

  bindEvent(
    channel,
    instance,
    CHANNEL_EVENTS.SEGMENT_UPDATED,
    wrapCallback(callbacks.onSegmentUpdated)
  )

  return { instance, channel, presenceChannel }
}

export const unsubscribeFromChannel = channelInstance => {
  if (!channelInstance) return
  const { channel, instance } = channelInstance

  if (channel) teardownChannelInstance(channel, instance)
}

export const unsubscribeFromThread = thread => {
  const { channel, presenceChannel, instance } = thread

  if (channel) teardownChannelInstance(channel, instance)
  if (presenceChannel) teardownChannelInstance(presenceChannel, instance)
}

export const publishToThread = ({
  channel,
  messageBody,
  messageCreatedAt,
  user,
  memberIds,
  messageThreadId,
  imageIds = [],
  imageUrls = [],
  videoUrl,
  audioUrl,
  audioWaveform,
  attachmentData = {},
  messageType = MESSAGE_TYPES.MESSAGE,
  messages = [],
  isRoot = false,
  sessionId = null,
  mentionedMembers = []
}) => {
  let messageData = {
    messageType,
    messageBody,
    messageCreatedAt,
    messageTs: messageCreatedAt,
    memberIds,
    messageThreadId,
    messages,
    isRoot,
    sessionId,
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
      avatar_urls: user.avatarUrls
    },
    mentionedMembers
  }

  if (messageType === MESSAGE_TYPES.IMAGE) {
    messageData = {
      ...messageData,
      ...{ imageIds: imageIds, imageUrls: imageUrls }
    }
  } else if (messageType === MESSAGE_TYPES.VIDEO) {
    messageData = {
      ...messageData,
      ...{ videoUrl: videoUrl }
    }
  } else if (messageType === MESSAGE_TYPES.AUDIO) {
    messageData = {
      ...messageData,
      ...{ audioUrl: audioUrl, audioWaveform: audioWaveform }
    }
  } else if (messageType === MESSAGE_TYPES.ATTACHMENT) {
    messageData = {
      ...messageData,
      ...{ attachmentData: attachmentData }
    }
  }

  channel.trigger(CHANNEL_EVENTS.MESSAGE, messageData)

  return messageData
}

export const removeFromThread = ({
  channel,
  user,
  messageTs,
  messageType = MESSAGE_TYPES.MESSAGE_DELETED,
  sessionId = null
}) => {
  const pusherData = {
    messageTs,
    messageType,
    user,
    sessionId
  }

  channel.trigger(CHANNEL_EVENTS.MESSAGE_DELETED, pusherData)

  return pusherData
}

export const reactMessage = ({
  channel,
  user,
  action,
  likedUserId,
  messageTs,
  messageType = MESSAGE_TYPES.MESSAGE_REACTION,
  sessionId = null
}) => {
  const pusherData = {
    action,
    messageTs,
    messageType,
    user,
    likedUserId,
    sessionId
  }

  channel.trigger(CHANNEL_EVENTS.MESSAGE_REACTION, pusherData)

  return pusherData
}
